import { endOfDay, format, startOfDay, subMonths } from 'date-fns';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useLodash } from '@/setup/lodash';

import { type FilterRecord, typeGuard } from '@/types/core';
import type { PostReportsData, PostReportsResponse } from '@/types/openapi-ts';
import type { AllFilterKeys, CurrentFilter } from '@/views/Dashboard/components/DashboardFilter.vue';
import type { FilterRecordExtended, FilterRecordExtendedCustom, FilterRecordExtendedNoParse, FilterRecordFilter } from '@/types/dashboard';


interface FilterItem {
  type: string;
  checkSupported: boolean;
  field: string;
  name: string;
  options: () => any;
  labelProp: string;
  valueProp: string;
  multiple: boolean;
  filterable: boolean;
  remote: boolean;
  method: ((q: string) => any) | null;
}

export function useDashboardFilter() {
  //#region composables
  const{ t } = useI18n();
  const api = useApi();
  const store = useTypedStore();
  const _ = useLodash();
  //#endregion

  //#region state
  const options = ref({
    playerCountryNameFilter: [] as PostReportsResponse['payload']['data'][number][],
    playerRegionNameFilter: [] as PostReportsResponse['payload']['data'][number][],
    trafficRegionNameFilter: [] as PostReportsResponse['payload']['data'][number][],
  });
  const today = ref(new Date());
  const isSelectLoading = ref(false);
  const isDisabled = ref(true);
  //#endregion

  //#region computed
  const reportsSettings = computed(() => store.getters['reports/reportsSettings']);

  const supportedDimension = computed(() => {
    return (reportsSettings.value.dimensions || []).map((dim) => dim.column_name);
  });

  const availableFilters = computed(() => {
    const allFilters = {
      playerFilter: {
        type: 'playerFilter',
        field: 'players_filter',
        name: t('ui.filters.playerFilter'),
        options: () => [
          {
            type: 'all',
            name: t('reports.players_type.all'),
          },
          {
            type: 'new',
            name: t('reports.players_type.new'),
          },
          {
            type: 'old',
            name: t('reports.players_type.old'),
          },
        ],
        labelProp: 'name',
        valueProp: 'type',
        multiple: false,
        filterable: false,
        remote: false,
        checkSupported: false,
        method: null,
      },
      trafficType: {
        type: 'trafficType',
        field: 'traffic_type',
        name: t('ui.filters.trafficType'),
        options: () => [
          {
            type: 'all',
            name: t('ui.filters.select.all'),
          },
          {
            type: 'affiliates',
            name: t('ui.filters.select.partners'),
          },
          {
            type: 'direct',
            name: t('ui.filters.select.direct'),
          },
        ],
        labelProp: 'name',
        valueProp: 'type',
        multiple: false,
        filterable: false,
        remote: false,
        checkSupported: false,
        method: null,
      },
      playerCountryNameFilter: {
        type: 'playerCountryNameFilter',
        checkSupported: true,
        field: 'player_country_code',
        name: t('ui.filters.playerCountryNameFilter'),
        options: () => options.value.playerCountryNameFilter,
        labelProp: 'player_country_name',
        valueProp: 'row_id',
        multiple: true,
        filterable: true,
        remote: true,
        method: (q: string) => {
          const dateFrom = startOfDay(subMonths(today.value, 1));
          const dateTo = format(today.value, 'yyyy-MM-dd HH:mm:ss');
          const query: PostReportsData['body'][string] = {
            search: q ? {
              player_country_name: {
                op: 'like',
                value: [q],
              },
            } : undefined,
            dimensions: ['player_country_name'],
            limit: 100,
            offset: 0,
            sorting: [{
              sort_by: 'player_country_name',
              sort_dir: 'asc',
            }],
            metrics: ['impressions_count'],
            from: format(startOfDay(dateFrom), 'yyyy-MM-dd HH:mm:ss'),
            to: format(endOfDay(dateTo), 'yyyy-MM-dd HH:mm:ss'),
          };
          isSelectLoading.value = true;
          api.getMainReport(query)
            .then((response) => {
              options.value.playerCountryNameFilter = response.data.payload.data;
            })
            .catch((error) => {
              console.log(error);
            }).finally(()=> {
            isSelectLoading.value = false;
          });
        },
      },
      playerRegionNameFilter: {
        type: 'playerRegionNameFilter',
        checkSupported: true,
        field: 'player_region_name',
        name: t('ui.filters.playerRegionNameFilter'),
        options: () => options.value.playerRegionNameFilter,
        labelProp: 'player_region_name',
        valueProp: 'row_id',
        multiple: true,
        filterable: true,
        remote: true,
        method: (q: string) => {
          const dateFrom = startOfDay(subMonths(today.value, 1));
          const dateTo = format(today.value, 'yyyy-MM-dd HH:mm:ss');

          const query: PostReportsData['body'][string]  = {
            search: q ? {
              player_region_name: {
                op: 'like',
                value: [q],
              },
            } : undefined,
            dimensions: ['player_region_name'],
            limit: 100,
            offset: 0,
            sorting: [{
              sort_by: 'player_region_name',
              sort_dir: 'asc',
            }],
            metrics: ['impressions_count'],
            from: format(startOfDay(dateFrom), 'yyyy-MM-dd HH:mm:ss'),
            to: format(endOfDay(dateTo), 'yyyy-MM-dd HH:mm:ss'),
          };
          isSelectLoading.value = true;
          api.getMainReport(query)
            .then((response) => {
              options.value.playerRegionNameFilter = response.data.payload.data;
            })
            .catch((error) => {
              console.log(error);
            }).finally(()=> {
            isSelectLoading.value = false;
          });
        },
      },
      trafficRegionNameFilter: {
        type: 'trafficRegionNameFilter',
        checkSupported: true,
        field: 'country_code',
        name: t('ui.filters.trafficRegionNameFilter'),
        options: () => options.value.trafficRegionNameFilter,
        labelProp: 'country_name',
        valueProp: 'row_id',
        multiple: true,
        filterable: true,
        remote: true,
        method: (q: string) => {
          const dateFrom = startOfDay(subMonths(today.value, 1));
          const dateTo = format(today.value, 'yyyy-MM-dd HH:mm:ss');
          const query: PostReportsData['body'][string] = {
            search: q ? {
              country_name: {
                op: 'like',
                value: [q],
              },
            } : undefined,
            dimensions: ['country_name'],
            limit: 100,
            offset: 0,
            sorting: [{
              sort_by: 'country_name',
              sort_dir: 'asc',
            }],
            metrics: ['impressions_count'],
            from: format(startOfDay(dateFrom), 'yyyy-MM-dd HH:mm:ss'),
            to: format(endOfDay(dateTo), 'yyyy-MM-dd HH:mm:ss'),
          };

          isSelectLoading.value = true;
          api.getMainReport(query)
            .then((response) => {
              options.value.trafficRegionNameFilter = response.data.payload.data;
            })
            .catch((error) => {
              console.log(error);
            }).finally(()=> {
            isSelectLoading.value = false;
          });
        },
      },
    } satisfies Record<string, FilterItem>;

    const keyAllFilters = Object.keys(allFilters) as (keyof typeof allFilters)[];

    return keyAllFilters.reduce((acc, value) => {
      if (!supportedDimension.value.includes(allFilters[value]?.labelProp as typeof supportedDimension.value[number])
        && allFilters[value]?.checkSupported) {
        return acc;
      }

      acc[value] = allFilters[value];
      return acc;
    }, {} as Record<keyof typeof allFilters, FilterItem>);
  });
  //#endregion

  const decodeFilterSelect = (dataApiFilter: FilterRecordExtended) => {
    const noParse: FilterRecordExtendedNoParse[] = ['traffic_type', 'players_filter'];

    const keyFilters = Object.keys(dataApiFilter.filters || {});
    const reducedObj = keyFilters.reduce((acc, val) => {
      acc[val] = dataApiFilter.filters![val]!;
      return acc;
    }, {} as FilterRecordFilter);
    const result = { ...reducedObj } as FilterRecordExtendedCustom;

    noParse.forEach((elParse) => {
      if (Object.keys(dataApiFilter).includes(elParse)) {
        result[elParse] = {
          value: dataApiFilter[elParse],
          op: null,
        };
      }
    });

    return result;
  };

  const handleDisabled = (v: any[]) => {
    isDisabled.value = false;
    isDisabled.value = v.some((e) => e.type === '' || e.values.length === 0);
  };

  const transformForFilterView = (filters: FilterRecord): CurrentFilter[] => {
    if (!_.isEmpty(filters)) {
      return Object.keys(filters).map((key) => ({
        type: _.find(availableFilters.value, (f) => f.field === key)?.type as AllFilterKeys || '',
        values: filters[key]!.value,
        op: filters[key]!.op,
      }))
    }
    return [];
  };

  const transformFromFilterView = (filters: CurrentFilter[]) => {
    const val: FilterRecordExtendedCustom = {};
    filters.forEach((filter) => {
      Object.assign(val, {
        [availableFilters.value[filter.type]?.field || '']: {
          value: filter.values,
          op: filter.op,
        },
      });
    });

    return val;
  };

  const parseFilter = (filterData: FilterRecord) => {
    const noParse: FilterRecordExtendedNoParse[] = ['traffic_type', 'players_filter'];
    const allKeyFilters = Object.keys(filterData);

    return allKeyFilters.reduce(
      (acc, val) => {
        if (typeGuard<FilterRecordExtendedNoParse>(val) && noParse.includes(val)) {
          acc[val] = filterData[val]!.value;
          return acc;
        }
        acc.filters![val] = {
          //@ts-expect-error может быть null
          op: filterData[val]!.op,
          value: filterData[val]!.value,
        };
        return acc;
      },
      { filters: {} } as FilterRecordExtended,
    );
  };

  return {
    availableFilters,
    isSelectLoading,
    reportsSettings,
    isDisabled,

    transformForFilterView,
    transformFromFilterView,
    decodeFilterSelect,
    parseFilter,
    handleDisabled,
  };
}
